// Custom requests, split by whether they still need something doing.
//
// One list sorted by date buried the two or three requests actually
// waiting on a reply among everything ever declined or turned into an
// order. Open requests are the working list; closed ones are history, kept
// visible but out of the way.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { adminDeleteInquiry, adminGetInquiries, describeApiError } from '../../services/api';
import type { Inquiry, InquiryStatus } from '../../types';
import { useConfirm } from '../../hooks/useConfirm';
import { ITEM_SOURCE_LABEL } from './orderUtils';

const STATUS_LABEL: Record<InquiryStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  quoted: 'Quoted',
  declined: 'Declined',
  converted: 'Converted',
};

// Reached the end of the road, one way or the other.
const CLOSED_STATUSES: InquiryStatus[] = ['declined', 'converted'];

const STATUS_STYLE: Record<InquiryStatus, string> = {
  new: 'bg-primary/10 text-primary',
  reviewed: 'bg-accent-sage/20 text-text',
  quoted: 'bg-accent-gold/20 text-text',
  declined: 'bg-background-dark text-text-light',
  converted: 'bg-green-100 text-green-900',
};

interface TableProps {
  title: string;
  hint: string;
  rows: Inquiry[];
  empty: string;
  onOpen: (id: string) => void;
  onDelete: (inquiry: Inquiry) => void;
  deleting?: string;
}

const InquiryTable: React.FC<TableProps> = ({
  title,
  hint,
  rows,
  empty,
  onOpen,
  onDelete,
  deleting,
}) => (
  <section className="mb-8">
    <div className="flex items-baseline gap-3 mb-2">
      <h2 className="text-lg font-medium">{title}</h2>
      <span className="text-xs text-text-light">
        {rows.length} · {hint}
      </span>
    </div>

    {rows.length === 0 ? (
      <div className="border border-dashed border-background-dark rounded p-6 text-center text-sm text-text-light">
        {empty}
      </div>
    ) : (
      <>
      {/* Phone layout. Five columns don't fit, and scrolling sideways hides
          the status — the thing you're scanning the list for. */}
      <ul className="sm:hidden space-y-2">
        {rows.map(i => (
          <li
            key={i.inquiry_id}
            className="bg-white border border-background-dark rounded p-3"
          >
            <button
              type="button"
              onClick={() => onOpen(i.inquiry_id)}
              className="w-full text-left"
            >
              <div className="flex justify-between items-start gap-3">
                <span className="font-medium truncate">{i.customer_name}</span>
                <span
                  className={`text-xs rounded-full px-2 py-0.5 shrink-0 ${STATUS_STYLE[i.status]}`}
                >
                  {STATUS_LABEL[i.status]}
                </span>
              </div>
              <div className="text-xs text-text-light truncate">{i.customer_email}</div>
              <div className="text-xs text-text-light mt-1">
                {ITEM_SOURCE_LABEL[i.item_source]} ·{' '}
                {new Date(i.created_at).toLocaleDateString()}
              </div>
            </button>
            <button
              type="button"
              onClick={() => onDelete(i)}
              disabled={deleting === i.inquiry_id}
              className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 disabled:opacity-40"
            >
              <Trash2 size={13} /> Delete
            </button>
          </li>
        ))}
      </ul>

      <table className="hidden sm:table w-full bg-white rounded border border-background-dark text-sm">
        <thead className="bg-background-dark/50">
          <tr>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">Item source</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Received</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {rows.map(i => (
            <tr
              key={i.inquiry_id}
              onClick={() => onOpen(i.inquiry_id)}
              className="border-t border-background-dark cursor-pointer hover:bg-background-dark/30 group"
            >
              <td className="px-3 py-2">{i.customer_name}</td>
              <td className="px-3 py-2">{i.customer_email}</td>
              <td className="px-3 py-2 text-xs">{ITEM_SOURCE_LABEL[i.item_source]}</td>
              <td className="px-3 py-2">
                <span className={`text-xs rounded-full px-2 py-0.5 ${STATUS_STYLE[i.status]}`}>
                  {STATUS_LABEL[i.status]}
                </span>
              </td>
              <td className="px-3 py-2 text-xs">
                {new Date(i.created_at).toLocaleDateString()}
              </td>
              <td className="px-3 py-2">
                <button
                  type="button"
                  // The row navigates, so the button has to stop the click
                  // reaching it or deleting would also open the page.
                  onClick={e => {
                    e.stopPropagation();
                    onDelete(i);
                  }}
                  disabled={deleting === i.inquiry_id}
                  aria-label={`Delete request from ${i.customer_name}`}
                  className="text-red-600 hover:bg-red-50 rounded p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-40"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </>
    )}
  </section>
);

const InquiriesManagement: React.FC = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    setLoading(true);
    // Always fetched whole: the split below is what the status filter used
    // to be for, and both tables need to be in view at once.
    adminGetInquiries()
      .then(setInquiries)
      .catch(e => setError(describeApiError(e, 'Failed to load inquiries')))
      .finally(() => setLoading(false));
  }, []);

  const remove = async (inquiry: Inquiry) => {
    const photos = inquiry.image_urls?.length ?? 0;
    const ok = await confirm({
      title: `Delete the request from ${inquiry.customer_name}?`,
      body: `This removes the request${
        photos ? ` and its ${photos} reference photo${photos === 1 ? '' : 's'}` : ''
      } for good. ${
        inquiry.status === 'converted'
          ? 'The order it became is not affected.'
          : 'This cannot be undone.'
      }`,
      confirmLabel: 'Delete request',
      tone: 'danger',
    });
    if (!ok) return;

    setDeleting(inquiry.inquiry_id);
    setError(undefined);
    try {
      await adminDeleteInquiry(inquiry.inquiry_id);
      setInquiries(prev => prev.filter(i => i.inquiry_id !== inquiry.inquiry_id));
    } catch (e: any) {
      setError(describeApiError(e, 'Delete failed'));
    } finally {
      setDeleting(undefined);
    }
  };

  const open = inquiries.filter(i => !CLOSED_STATUSES.includes(i.status));
  const closed = inquiries.filter(i => CLOSED_STATUSES.includes(i.status));

  return (
    <div>
      {confirmDialog}
      <h1 className="text-2xl font-semibold mb-6">Inquiries</h1>

      {loading && <div className="text-text-light">Loading…</div>}
      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {!loading && !error && (
        <>
          <InquiryTable
            title="Open"
            hint="new, reviewed and quoted"
            rows={open}
            empty="Nothing waiting on you right now."
            onOpen={id => navigate(`/admin/inquiries/${id}`)}
            onDelete={remove}
            deleting={deleting}
          />
          <InquiryTable
            title="Closed"
            hint="declined and converted"
            rows={closed}
            empty="No finished requests yet."
            onOpen={id => navigate(`/admin/inquiries/${id}`)}
            onDelete={remove}
            deleting={deleting}
          />
        </>
      )}
    </div>
  );
};

export default InquiriesManagement;
